<template>
  <div class="related-items-list">
    <div v-for="(item, index) in itemsToDisplay" :key="index">
      <div
        v-if="showEntityName(index)"
        class="related-items-list__row-entity-name"
      >{{item.entity.name}}</div>
      <div
        :class="{'related-items-list__row-item-inactive' : !item.isActive }"
        class="related-items-list__row-item"
        @click.exact="$emit('item-clicked', item)"
        @click.meta="openItemDetailInNewWindow(item)"
      >
        <span><strong>#{{item.refNumber}}:</strong> {{item.title}}</span>
        <div class="related-items-list__row-item__chicklets">
          <template v-if="!$screen.isMobile($mq)">
            <due-date-chicklet :due-date="item.dueDate" />
            <priority-chicklet :priority="item.priority" hide-icon />
            <status-chicklet :status="item.status" />
          </template>
          <avatar :person="item.assignedTo" :size="24" />
        </div>
      </div>
    </div>

    <div
      v-if="canManageItems"
      class="related-items-list__add-link"
      @click="$emit('add-link-clicked')"
    >+ {{addLinkText}}</div>
  </div>
</template> 

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Mixins */

/* Components */

/* Helpers */

import RouteAssistant from '@/helpers/route-assistant'

/* Data types */
import { EEntities } from '@/types'
import { ILinkedItemForListing } from '@/api/types/response.types'

/* API */

export default defineComponent({
  name: 'RelatedItemsList',

  props: {
    items: {
      type: Array as () => ILinkedItemForListing[],
      default: (): ILinkedItemForListing[] => [],
    },
    entityType: {
      type: String as () => EEntities,
      default: EEntities.MAILBOXES,
    },
    groupByEntity: {
      type: Boolean,
      default: false,
    },
    addLinkText: {
      type: String,
      default: '',
    },
  },

  emits: ['add-link-clicked', 'item-clicked'],

  data() {
    return {
      showAllItems: false,
      showModal: false,
    }
  },

  computed: {
    itemsToDisplay(): ILinkedItemForListing[] {
      return this.sortByEntityAndActive(this.items)
    },
    canManageItems(): boolean {
      return this.addLinkText.length > 0
    },
  },

  methods: {
    iconClass(item: ILinkedItemForListing): string {
      let result = ''

      if (this.entityType === EEntities.MAILBOXES) {
        result = 'ddi-conversation'
      } else {
        result = 'ddi-task'
      }

      if (!item.entityAccessible) {
        result += '-locked'
      }

      return result
    },
    openItemDetailInNewWindow(item: ILinkedItemForListing) {
      if (this.entityType === EEntities.MAILBOXES) {
        RouteAssistant.goToConversation(
          this.$router,
          this.$route.params.accountId,
          item.entity.id,
          item.id,
          item.entity.name,
          item.refNumber,
          item.title,
          true,
        )
      } else {
        RouteAssistant.goToTask(
          this.$router,
          this.$route.params.accountId,
          item.entity.id,
          item.id,
          this.$permissions,
          item.entity.name,
          item.refNumber,
          item.title,
          true,
        )
      }
    },
    // Show entity name if the indexed item has a different entity than its predecessor
    showEntityName(index: number): boolean {
      if (!this.groupByEntity) {
        return false
      }

      if (index === 0) {
        return true
      }

      if (this.items[index - 1].entity.id !== this.items[index].entity.id) {
        return true
      }
    },

    sortByEntityAndActive(items: ILinkedItemForListing[]) {
      items.sort((a: ILinkedItemForListing, b: ILinkedItemForListing) => {
        if (a.entity.name.toLowerCase() < b.entity.name.toLowerCase()) {
          return -1
        }
        if (a.entity.name.toLowerCase() > b.entity.name.toLowerCase()) {
          return 1
        }
        if (a.isActive && !b.isActive) {
          return -1
        }
        if (!a.isActive && b.isActive) {
          return 1
        }
        return 0
      })
      return items
    },
  },
})
</script>

<style lang="scss">
.related-items-list {
  border: solid 1px $light-blue-grey-two;
  border-radius: 8px;
  overflow-y: hidden;
  overflow: auto;
  max-height: 360px;
  min-height: 40px;

  &__add-link {
    padding: 8px 12px;
    cursor: pointer;
    @include lettering--p-sm;
    color: var(--color-text-primary);
  }

  &__row-entity-name {
    background-color: var(--color-background-secondary);
    padding: 6px 12px;
    border-bottom: solid 1px $light-blue-grey-two;
    @include lettering--h5;
    color: var(--color-text-third);
  }

  &__row-item {
    display: flex;
    justify-content: space-evenly;
    align-items: center;
    padding: 7px 12px;
    border-bottom: solid 1px $light-blue-grey-two;
    cursor: pointer;
    @include lettering--p;

    &-inactive {
      > * {
        opacity: 0.5;
      }

      &:hover {
        background-color: var(--color-background-hover);
      }
    }

    &:hover {
      background-color: var(--color-background-hover);
    }

    > span {
      padding-right: 8px;
      margin-right: auto;
      cursor: pointer;
      @include lettering--p-sm;
    }

    &__chicklets {
      display: flex;
      align-items: center;
      @include flex-row-horizontal-spacing(8px);
    }

    &__unlink {
      color: var(--color-text-third);
      margin-left: 4px;
    }

    > .ddi {
      color: $color--blue;
      padding-right: 4px;
    }

    > .ddi-task-locked,
    > .ddi-conversation-locked {
      color: $color--gray-60 !important;
    }
  }
}
</style>
